import { Button } from "@/components/ui/button";
import { trpc, RouterOutputs } from "@/hooks/trpc";
import {
  PlusIcon,
  Trash2Icon,
  ClipboardListIcon,
  Loader2Icon,
} from "lucide-react";
import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { DataTable, DataTableColumnDef } from "@/components/ui/data-table";
import { confirm } from "@/components/global-alert-dialog";
import { cn } from "@/lib/utils";
import { BrothSummary } from "@/features/tank-board/broth-summary";
import {
  deriveSessionName,
  withSessionLabel,
} from "@/features/harvest-journal/lib/session-name";
import { NewPlannedSessionDialog } from "@/features/harvest-journal/components/new-planned-session-dialog";

export const Route = createFileRoute("/harvests/")({
  component: RouteComponent,
});

type HarvestWithSessionDetails =
  RouterOutputs["harvest"]["listSessions"][number];

// Also the column's search value, so the label is searchable too.
function displayedSessionName(row: HarvestWithSessionDetails): string {
  return withSessionLabel(
    row.session_name ??
      deriveSessionName({
        id: row.session_id,
        planned_date: row.session_planned_date,
        started_at: row.session_started_at,
        responsible_operator_name: row.session_responsible_operator_name,
      }),
    row.session_label,
  );
}

function RouteComponent() {
  const navigate = Route.useNavigate();

  const utils = trpc.useUtils();
  const { data: harvestsWithSessions } =
    trpc.harvest.listSessions.useQuery();

  const deleteSessionMutation = trpc.harvest.deleteSession2.useMutation({
    onSuccess: () => {
      utils.harvest.listSessions.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to delete harvest session: ${error.message}`);
    },
  });

  const columns = useMemo<DataTableColumnDef<HarvestWithSessionDetails, unknown>[]>(
    () => [
      {
        id: "session_name",
        accessorFn: displayedSessionName,
        // Two planned sessions can derive the same name; group by identity.
        getGroupingValue: (row) => row.session_id,
        header: "Session",
        enableGrouping: true,
        cell: ({ row }) => (
          <Link
            to="/harvests/$session_id"
            params={{ session_id: row.original.session_id }}
            className="hover:underline"
          >
            {displayedSessionName(row.original)}
          </Link>
        ),
        size: 300,
        meta: {
          leafCell: ({ row }) => (
            <div className="pl-8 flex items-center gap-2">
              <div>{row.original.harvest_name}</div>
            </div>
          ),
        },
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (row) => {
          if (row.session_ended_at) return "Completed";
          if (row.session_started_at) return "In Progress";
          return "Planned";
        },
        cell: ({ getValue }) => {
          const status = getValue<string>();
          const colorClass =
            status === "Completed"
              ? "bg-green-9"
              : status === "In Progress"
                ? "bg-yellow-9"
                : "bg-olive-8";
          return (
            <span className="flex items-center gap-1.5 text-sm">
              <span
                className={cn("w-1.5 h-1.5 rounded-full", colorClass)}
              />
              {status}
            </span>
          );
        },
        enableGrouping: false,
        aggregationFn: "unique",
        size: 120,
      },
      {
        id: "harvest_name",
        accessorKey: "harvest_name",
        header: "Harvest Name",
        cellTypeId: "string",
        enableSorting: true,
      },
      {
        id: "planned_date",
        accessorKey: "session_planned_date",
        header: "Planned Date",
        cellTypeId: "date",
        enableSorting: true,
        aggregationFn: "max",
      },
      {
        id: "session_started_at",
        accessorKey: "session_started_at",
        header: "Started",
        cellTypeId: "date",
        enableSorting: true,
        aggregationFn: "max",
      },
      {
        id: "protocol_name",
        accessorKey: "protocol_name",
        header: "Protocol",
        cellTypeId: "string",
        enableSorting: true,
        aggregationFn: "unique",
        enableColumnFilter: true,
      },
      {
        id: "fermentation_name",
        accessorKey: "fermentation_name",
        header: "Source Fermentation",
        cell: ({ row }) => {
          const groupId = row.original.fermentation_group_id;
          const name = row.original.fermentation_name;
          if (!groupId || !name) return null;
          return (
            <Link
              to="/fermentations/$group_id"
              params={{ group_id: groupId }}
              className="hover:underline"
            >
              {name}
            </Link>
          );
        },
        aggregationFn: "unique",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: "assigned_operator_name",
        accessorKey: "assigned_operator_name",
        header: "Assigned Operator",
        cellTypeId: "string",
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: "delete",
        header: "Delete",
        cell: ({ row }) => (
          <Button
            variant="destructiveGhost"
            size="icon"
            onClick={async () => {
              if (
                await confirm({
                  title: "Delete Harvest Session",
                  description:
                    "Are you sure you want to delete this harvest session? This action cannot be undone.",
                  destructive: true,
                })
              ) {
                deleteSessionMutation.mutate(row.original.session_id);
              }
            }}
            disabled={deleteSessionMutation.isPending}
          >
            {deleteSessionMutation.isPending ? (
              <Loader2Icon className="size-4 animate-spin" />
            ) : (
              <Trash2Icon className="size-4" />
            )}
          </Button>
        ),
        size: 100,
        enableHiding: false,
        enableGrouping: false,
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          groupHeaderOnly: true,
        },
      },
    ],
    [navigate],
  );

  return (
    <div className="flex flex-col gap-2 mt-2 h-full min-h-0 grow container mx-auto">
      <BrothSummary />
      <DataTable
        columns={columns}
        data={harvestsWithSessions ?? []}
        defaultColumnVisibility={{
          harvest_name: false,
          assigned_operator_name: false,
        }}
        leftCustomToolbar={
          <>
            <NewPlannedSessionDialog>
              <Button variant="outline">
                <PlusIcon className="size-4 mr-2" />
                New Session
              </Button>
            </NewPlannedSessionDialog>
            <Link to="/harvests/protocols">
              <Button variant="outline">
                <ClipboardListIcon className="size-4 mr-2" />
                Protocols
              </Button>
            </Link>
          </>
        }
        defaultExpanded
        defaultGrouping={["session_name"]}
        searchPlaceholder="Search harvests..."
        enableGrouping={true}
        hardcodedGrouping={true}
        enableColumnResizing={true}
        maxHeight="none"
        className="grow min-h-0 flex-1"
      />
    </div>
  );
}
